import React from 'react';
import { Grid, Box, Typography, Tooltip, IconButton } from '@mui/material';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import { AccessTime, Error, Settings } from '@mui/icons-material';
import { styled, keyframes } from '@mui/system';
import { handleToggleDeleteOptimizationDialog, handleToggleFailedOptimizationModal, handleToggleOptimizationDetailsDialog } from '../../store/optimization-slice/optimization';

enum OptimizationStatusEnum
{
    Waiting = 10,
    Running = 20,
    Completed = 40,
    Failed = 50
}

interface OptimizationStatusActionsProps {
    params: any;
    dispatch: any;
    t: any;
}

const statusColorMap: Record<OptimizationStatusEnum, string> = {
    [OptimizationStatusEnum.Waiting]: "#787878",
    [OptimizationStatusEnum.Running]: "#49c44d",
    [OptimizationStatusEnum.Failed]: "#f44336",
    [OptimizationStatusEnum.Completed]: ""
};

const pulse = keyframes`
    0% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); opacity: 0.5; }
`;

const spin = keyframes`
    100% { transform: rotate(360deg); }
`;

const WaitingIcon = styled(AccessTime)(({ theme }) => ({
    animation: `${pulse} 1s infinite`,
    color: statusColorMap[OptimizationStatusEnum.Waiting],
}));

const FailedIcon = styled(Error)(({ theme }) => ({
    color: statusColorMap[OptimizationStatusEnum.Failed],
}));

const RunningIcon = styled(Settings)(({ theme }) => ({
    animation: `${spin} 1.5s linear infinite`,
    color: statusColorMap[OptimizationStatusEnum.Running],
}));

const getStatusIcon = (status: OptimizationStatusEnum) => {
    switch (status) {
      case OptimizationStatusEnum.Waiting:
        return <WaitingIcon />;
    case OptimizationStatusEnum.Failed:
        return <FailedIcon />;
    case OptimizationStatusEnum.Running:
        return <RunningIcon />;
    default:
        return null;
    }
};
const OptimizationStatusActions: React.FC<OptimizationStatusActionsProps> = ({ params, dispatch, t }) => {
    const renderStatus = (
        icon: React.ReactNode,
        text: string,
        onClick?: () => void
    ) => (
        <Grid container spacing={1} justifyContent="center" alignItems="center">
            <Grid item>
                <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    onClick={onClick}
                    style={{ cursor: onClick ? 'pointer' : 'default' }}
                >
                    {icon}
                    <Typography variant="body2">{t(text)}</Typography>
                </Box>
            </Grid>
        </Grid>
    );

    const renderFailedActions = (text: string) => (
        <Box display="flex" gap={1} justifyContent="center">
            <Tooltip title={t('FAILED_ICON_TOOLTIP')}>
                <Box display="flex" alignItems="center">
                    <IconButton onClick={() => dispatch(handleToggleFailedOptimizationModal({
                        isOpen: true,
                        body: { message: t(params.row.responseError), description: params.row.responseErrorDetails }
                    }))}>
                        <FailedIcon />
                    </IconButton>
                </Box>
            </Tooltip>
            <Tooltip title={t('OPTIMIZATIONS_DELETE_TOOLTIP')}>
                <Box display="flex" alignItems="center">
                    <IconButton onClick={() => dispatch(handleToggleDeleteOptimizationDialog({ isOpen: true, body: params.row }))}>
                        <DeleteForeverTwoToneIcon className="delete-icon" />
                    </IconButton>
                </Box>
            </Tooltip>
        </Box>
    );

    const renderCompletedActions = () => (
        <Box display="flex" gap={1} justifyContent="center">
            <Tooltip title={t('OPTIMIZATIONS_DETAILS_TOOLTIP')}>
                <IconButton onClick={() => {
                    dispatch(handleToggleOptimizationDetailsDialog({ isOpen: true, body: params.row }));
                }}>
                    <InfoTwoToneIcon className='info-icon' />
                </IconButton>
            </Tooltip>
            <Tooltip title={t('OPTIMIZATIONS_DELETE_TOOLTIP')}>
                <IconButton onClick={() => {
                    dispatch(handleToggleDeleteOptimizationDialog({ isOpen: true, body: params.row }))
                }}>
                    <DeleteForeverTwoToneIcon className="delete-icon" />
                </IconButton>
            </Tooltip>
        </Box>
    );

    switch (params.row.status) {
        case OptimizationStatusEnum.Waiting:
        case OptimizationStatusEnum.Running:
            return renderStatus(
                getStatusIcon(params.row.status),
                params.row.statusText
            );

        case OptimizationStatusEnum.Failed:
            return renderFailedActions(params.row.statusText);

        case OptimizationStatusEnum.Completed:
            return renderCompletedActions();

        default:
            return null;
    }
};

export default OptimizationStatusActions;